/**
 * Project Settings Page
 * Full project configuration including AI settings, budget, integrations
 */

import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { projectsApi } from '../utils/api';
import {
    ArrowLeft,
    Save,
    Trash2,
    Globe,
    DollarSign,
    Sparkles,
    CheckCircle,
    AlertCircle,
    Loader2,
    Settings,
    Webhook,
    Copy,
    Package,
    Zap,
    Lock
} from 'lucide-react';

type Tab = 'general' | 'modules' | 'ai' | 'budget' | 'integrations' | 'danger';

// Module catalog with metadata
const MODULE_CATALOG = [
    { type: 'seo', name: 'SEO Optimizer', description: 'Keyword tracking, audits, content optimization', icon: '🔍', category: 'marketing' },
    { type: 'social', name: 'Social Media', description: 'Multi-platform scheduling and analytics', icon: '📱', category: 'marketing' },
    { type: 'ads', name: 'Paid Ads Manager', description: 'Google & Facebook Ads automation', icon: '📢', category: 'marketing' },
    { type: 'email', name: 'Email Marketing', description: 'Campaigns, automation, list management', icon: '📧', category: 'marketing' },
    { type: 'analytics', name: 'Analytics Pro', description: 'Advanced reporting and dashboards', icon: '📊', category: 'business' },
    { type: 'payments', name: 'Payment Processing', description: 'Invoicing and payment integration', icon: '💳', category: 'business' },
    { type: 'content', name: 'AI Content Writer', description: 'Generate blogs, ads, social posts', icon: '✍️', category: 'creative' },
    { type: 'crm', name: 'CRM Integration', description: 'Lead tracking and customer management', icon: '👥', category: 'business' },
    { type: 'chatbot', name: 'AI Chatbot', description: 'Website chat and lead capture', icon: '🤖', category: 'marketing' },
    { type: 'reviews', name: 'Review Manager', description: 'Collect and respond to reviews', icon: '⭐', category: 'marketing' },
];

interface ProjectData {
    name: string;
    description: string;
    websiteUrl: string;
    industry: string;
    monthlyBudget: number | null;
    currency: string;
    aiSettings: {
        autoContent: boolean;
        autoOptimize: boolean;
        contentTone: string;
        targetAudience: string;
        preferredAI: string;
        maxDailyRequests: number;
    };
}

const industries = [
    { value: 'ecommerce', label: 'E-commerce' },
    { value: 'saas', label: 'SaaS / Software' },
    { value: 'agency', label: 'Agency / Consulting' },
    { value: 'healthcare', label: 'Healthcare' },
    { value: 'finance', label: 'Finance' },
    { value: 'education', label: 'Education' },
    { value: 'other', label: 'Other' },
];

const contentTones = [
    { value: 'professional', label: 'Professional' },
    { value: 'friendly', label: 'Friendly & Casual' },
    { value: 'formal', label: 'Formal' },
    { value: 'playful', label: 'Playful & Fun' },
    { value: 'authoritative', label: 'Authoritative' },
    { value: 'empathetic', label: 'Empathetic & Supportive' },
];

const currencies = ['USD', 'EUR', 'GBP', 'CAD', 'AUD', 'JPY', 'INR'];

export default function ProjectSettings() {
    const { projectId } = useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [activeTab, setActiveTab] = useState<Tab>('general');
    const [saved, setSaved] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState('');

    const [data, setData] = useState<ProjectData>({
        name: '',
        description: '',
        websiteUrl: '',
        industry: 'other',
        monthlyBudget: null,
        currency: 'USD',
        aiSettings: {
            autoContent: false,
            autoOptimize: false,
            contentTone: 'professional',
            targetAudience: '',
            preferredAI: 'auto',
            maxDailyRequests: 100,
        }
    });

    const { data: projectData, isLoading } = useQuery({
        queryKey: ['project', projectId],
        queryFn: () => projectsApi.get(projectId!),
        enabled: !!projectId,
    });

    // Load project data into form
    useEffect(() => {
        if (projectData?.data?.project) {
            const p = projectData.data.project;
            setData({
                name: p.name || '',
                description: p.description || '',
                websiteUrl: p.websiteUrl || '',
                industry: p.industry || 'other',
                monthlyBudget: p.monthlyBudget || null,
                currency: p.currency || 'USD',
                aiSettings: p.aiSettings ? (typeof p.aiSettings === 'string' ? JSON.parse(p.aiSettings) : p.aiSettings) : {
                    autoContent: false,
                    autoOptimize: false,
                    contentTone: 'professional',
                    targetAudience: '',
                    preferredAI: 'auto',
                    maxDailyRequests: 100,
                }
            });
        }
    }, [projectData]);

    const updateMutation = useMutation({
        mutationFn: (updateData: Partial<ProjectData>) =>
            projectsApi.update(projectId!, updateData),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['project', projectId] });
            setSaved(true);
            setTimeout(() => setSaved(false), 3000);
        }
    });

    const deleteMutation = useMutation({
        mutationFn: () => projectsApi.delete(projectId!),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['projects'] });
            navigate('/projects');
        }
    });

    const handleSave = () => {
        updateMutation.mutate(data);
    };

    const handleDelete = () => {
        if (deleteConfirm === data.name) {
            deleteMutation.mutate();
        }
    };

    const project = projectData?.data?.project;
    const projectModules = projectData?.data?.modules || [];

    // Get available modules from org (what they've paid for)
    // For now, simulate with all modules - in production, fetch from /modules/organization
    const orgModules = MODULE_CATALOG.map(m => m.type);

    // Track which modules are enabled for this project
    const [enabledModules, setEnabledModules] = useState<Record<string, boolean>>({});

    // Initialize module states from project data
    useEffect(() => {
        if (projectModules.length > 0) {
            const enabled: Record<string, boolean> = {};
            projectModules.forEach((m: any) => {
                enabled[m.type] = m.enabled;
            });
            setEnabledModules(enabled);
        }
    }, [projectModules]);

    const toggleModuleMutation = useMutation({
        mutationFn: async ({ moduleType, enabled: _enabled }: { moduleType: string; enabled: boolean }) => {
            // Use the existing toggleModule API
            return projectsApi.toggleModule(projectId!, moduleType);
        },
        onMutate: async ({ moduleType, enabled }) => {
            // Optimistic update
            setEnabledModules(prev => ({ ...prev, [moduleType]: enabled }));
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['project', projectId] });
        }
    });

    const tabs = [
        { id: 'general' as Tab, label: 'General', icon: Settings },
        { id: 'modules' as Tab, label: 'Modules', icon: Package },
        { id: 'ai' as Tab, label: 'AI Settings', icon: Sparkles },
        { id: 'budget' as Tab, label: 'Budget', icon: DollarSign },
        { id: 'integrations' as Tab, label: 'Integrations', icon: Webhook },
        { id: 'danger' as Tab, label: 'Danger Zone', icon: AlertCircle },
    ];

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-primary-400" />
            </div>
        );
    }

    if (!project) {
        return (
            <div className="text-center py-20">
                <h2 className="text-xl font-semibold mb-2">Project not found</h2>
                <Link to="/projects" className="text-primary-400 hover:underline">
                    Back to Projects
                </Link>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <Link
                        to={`/projects/${projectId}`}
                        className="p-2 hover:bg-dark-800 rounded-lg"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </Link>
                    <div>
                        <h1 className="text-2xl font-bold">Project Settings</h1>
                        <p className="text-dark-400">{project.name}</p>
                    </div>
                </div>
                <button
                    onClick={handleSave}
                    disabled={updateMutation.isPending}
                    className="btn btn-primary flex items-center gap-2"
                >
                    {updateMutation.isPending ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                    ) : saved ? (
                        <CheckCircle className="w-4 h-4" />
                    ) : (
                        <Save className="w-4 h-4" />
                    )}
                    {saved ? 'Saved!' : 'Save Changes'}
                </button>
            </div>

            <div className="flex flex-col lg:flex-row gap-6">
                {/* Sidebar */}
                <div className="lg:w-56 space-y-1">
                    {tabs.map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${activeTab === tab.id
                                ? tab.id === 'danger'
                                    ? 'bg-red-500/20 text-red-400'
                                    : 'bg-primary-600/20 text-primary-400'
                                : tab.id === 'danger'
                                    ? 'hover:bg-red-500/10 text-dark-400'
                                    : 'hover:bg-dark-800 text-dark-300'
                                }`}
                        >
                            <tab.icon className="w-5 h-5" />
                            {tab.label}
                        </button>
                    ))}
                </div>

                {/* Content */}
                <div className="flex-1">
                    {/* General Settings */}
                    {activeTab === 'general' && (
                        <div className="card space-y-6">
                            <h2 className="text-xl font-semibold">General Settings</h2>

                            <div>
                                <label className="label">Project Name</label>
                                <input
                                    type="text"
                                    value={data.name}
                                    onChange={(e) => setData({ ...data, name: e.target.value })}
                                    className="input"
                                    placeholder="My Project"
                                />
                            </div>

                            <div>
                                <label className="label">Description</label>
                                <textarea
                                    value={data.description}
                                    onChange={(e) => setData({ ...data, description: e.target.value })}
                                    className="input min-h-[100px]"
                                    placeholder="Describe your project..."
                                />
                            </div>

                            <div>
                                <label className="label">Website URL</label>
                                <div className="flex gap-2">
                                    <div className="relative flex-1">
                                        <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-dark-400" />
                                        <input
                                            type="url"
                                            value={data.websiteUrl}
                                            onChange={(e) => setData({ ...data, websiteUrl: e.target.value })}
                                            className="input pl-10"
                                            placeholder="https://example.com"
                                        />
                                    </div>
                                    {project.websiteVerified ? (
                                        <span className="flex items-center gap-1 px-3 py-2 bg-green-500/20 text-green-400 rounded-lg text-sm">
                                            <CheckCircle className="w-4 h-4" /> Verified
                                        </span>
                                    ) : (
                                        <button className="btn btn-secondary">Verify</button>
                                    )}
                                </div>
                            </div>

                            <div>
                                <label className="label">Industry</label>
                                <select
                                    value={data.industry}
                                    onChange={(e) => setData({ ...data, industry: e.target.value })}
                                    className="input"
                                >
                                    {industries.map((ind) => (
                                        <option key={ind.value} value={ind.value}>
                                            {ind.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="pt-4 border-t border-dark-700">
                                <h3 className="font-medium mb-3">Project Status</h3>
                                <div className="flex gap-3">
                                    {['active', 'paused', 'archived'].map((status) => (
                                        <button
                                            key={status}
                                            className={`px-4 py-2 rounded-lg border capitalize transition-all ${project.status === status
                                                ? status === 'active'
                                                    ? 'border-green-500 bg-green-500/20 text-green-400'
                                                    : status === 'paused'
                                                        ? 'border-yellow-500 bg-yellow-500/20 text-yellow-400'
                                                        : 'border-dark-500 bg-dark-700 text-dark-300'
                                                : 'border-dark-700 hover:border-dark-600'
                                                }`}
                                        >
                                            {status}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Modules - Enable/Disable */}
                    {activeTab === 'modules' && (
                        <div className="card space-y-6">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center">
                                        <Package className="w-5 h-5 text-white" />
                                    </div>
                                    <div>
                                        <h2 className="text-xl font-semibold">Project Modules</h2>
                                        <p className="text-sm text-dark-400">Enable or disable modules for this project</p>
                                    </div>
                                </div>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => {
                                            const allEnabled: Record<string, boolean> = {};
                                            MODULE_CATALOG.forEach(m => { allEnabled[m.type] = true; });
                                            setEnabledModules(allEnabled);
                                        }}
                                        className="btn btn-secondary text-sm flex items-center gap-2"
                                    >
                                        <Zap className="w-4 h-4" /> Enable All
                                    </button>
                                </div>
                            </div>

                            {/* Quick Status */}
                            <div className="flex gap-4 p-4 bg-dark-800/50 rounded-lg">
                                <div className="text-center">
                                    <p className="text-2xl font-bold text-green-400">
                                        {Object.values(enabledModules).filter(Boolean).length}
                                    </p>
                                    <p className="text-xs text-dark-400">Active</p>
                                </div>
                                <div className="text-center">
                                    <p className="text-2xl font-bold text-dark-400">
                                        {MODULE_CATALOG.length - Object.values(enabledModules).filter(Boolean).length}
                                    </p>
                                    <p className="text-xs text-dark-400">Disabled</p>
                                </div>
                                <div className="text-center">
                                    <p className="text-2xl font-bold text-primary-400">
                                        {orgModules.length}
                                    </p>
                                    <p className="text-xs text-dark-400">Available</p>
                                </div>
                            </div>

                            {/* Module List */}
                            <div className="space-y-3">
                                {MODULE_CATALOG.map((module) => {
                                    const hasAccess = orgModules.includes(module.type);
                                    const isEnabled = enabledModules[module.type] || false;
                                    const existingModule = projectModules.find((pm: any) => pm.type === module.type);

                                    return (
                                        <div
                                            key={module.type}
                                            className={`flex items-center justify-between p-4 rounded-lg border transition-all ${isEnabled
                                                ? 'bg-green-500/10 border-green-500/30'
                                                : hasAccess
                                                    ? 'bg-dark-800/50 border-dark-700 hover:border-dark-600'
                                                    : 'bg-dark-900/50 border-dark-800 opacity-60'
                                                }`}
                                        >
                                            <div className="flex items-center gap-4">
                                                <span className="text-2xl">{module.icon}</span>
                                                <div>
                                                    <div className="flex items-center gap-2">
                                                        <p className="font-medium">{module.name}</p>
                                                        {!hasAccess && (
                                                            <span className="flex items-center gap-1 px-2 py-0.5 bg-yellow-500/20 text-yellow-400 rounded text-xs">
                                                                <Lock className="w-3 h-3" /> Locked
                                                            </span>
                                                        )}
                                                        {existingModule?.status === 'active' && isEnabled && (
                                                            <span className="flex items-center gap-1 px-2 py-0.5 bg-green-500/20 text-green-400 rounded text-xs">
                                                                <CheckCircle className="w-3 h-3" /> Running
                                                            </span>
                                                        )}
                                                    </div>
                                                    <p className="text-sm text-dark-400">{module.description}</p>
                                                </div>
                                            </div>

                                            <div className="flex items-center gap-3">
                                                {hasAccess ? (
                                                    <>
                                                        <button
                                                            onClick={() => {
                                                                toggleModuleMutation.mutate({
                                                                    moduleType: module.type,
                                                                    enabled: !isEnabled
                                                                });
                                                            }}
                                                            className={`relative w-14 h-8 rounded-full transition-colors ${isEnabled ? 'bg-green-500' : 'bg-dark-600'
                                                                }`}
                                                        >
                                                            <span className={`absolute top-1 w-6 h-6 bg-white rounded-full transition-all ${isEnabled ? 'left-7' : 'left-1'
                                                                }`} />
                                                        </button>
                                                        {isEnabled && (
                                                            <button className="p-2 hover:bg-dark-700 rounded" title="Configure">
                                                                <Settings className="w-4 h-4 text-dark-400" />
                                                            </button>
                                                        )}
                                                    </>
                                                ) : (
                                                    <Link
                                                        to="/settings/billing"
                                                        className="btn btn-secondary text-sm"
                                                    >
                                                        Unlock
                                                    </Link>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Tip */}
                            <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                                <p className="text-sm text-blue-300">
                                    <strong>💡 Tip:</strong> Disabled modules won't consume AI requests or budget.
                                    Enable only what you need to optimize costs.
                                </p>
                            </div>
                        </div>
                    )}

                    {/* AI Settings */}
                    {activeTab === 'ai' && (
                        <div className="card space-y-6">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
                                    <Sparkles className="w-5 h-5 text-white" />
                                </div>
                                <div>
                                    <h2 className="text-xl font-semibold">AI Settings</h2>
                                    <p className="text-sm text-dark-400">Configure how AI assists this project</p>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-4">
                                    <label className="flex items-center justify-between p-4 bg-dark-800/50 rounded-lg cursor-pointer">
                                        <div>
                                            <p className="font-medium">Auto-generate Content</p>
                                            <p className="text-sm text-dark-400">AI creates content suggestions automatically</p>
                                        </div>
                                        <input
                                            type="checkbox"
                                            checked={data.aiSettings.autoContent}
                                            onChange={(e) => setData({
                                                ...data,
                                                aiSettings: { ...data.aiSettings, autoContent: e.target.checked }
                                            })}
                                            className="w-5 h-5 rounded"
                                        />
                                    </label>

                                    <label className="flex items-center justify-between p-4 bg-dark-800/50 rounded-lg cursor-pointer">
                                        <div>
                                            <p className="font-medium">Auto-optimize Campaigns</p>
                                            <p className="text-sm text-dark-400">AI optimizes ads and campaigns</p>
                                        </div>
                                        <input
                                            type="checkbox"
                                            checked={data.aiSettings.autoOptimize}
                                            onChange={(e) => setData({
                                                ...data,
                                                aiSettings: { ...data.aiSettings, autoOptimize: e.target.checked }
                                            })}
                                            className="w-5 h-5 rounded"
                                        />
                                    </label>
                                </div>

                                <div className="space-y-4">
                                    <div>
                                        <label className="label">Preferred AI Model</label>
                                        <select
                                            value={data.aiSettings.preferredAI}
                                            onChange={(e) => setData({
                                                ...data,
                                                aiSettings: { ...data.aiSettings, preferredAI: e.target.value }
                                            })}
                                            className="input"
                                        >
                                            <option value="auto">Auto (Best for task)</option>
                                            <option value="gpt4">GPT-4</option>
                                            <option value="claude">Claude 3</option>
                                            <option value="gemini">Gemini Pro</option>
                                        </select>
                                    </div>

                                    <div>
                                        <label className="label">Max Daily AI Requests</label>
                                        <input
                                            type="number"
                                            value={data.aiSettings.maxDailyRequests}
                                            onChange={(e) => setData({
                                                ...data,
                                                aiSettings: { ...data.aiSettings, maxDailyRequests: parseInt(e.target.value) || 100 }
                                            })}
                                            className="input"
                                            min={10}
                                            max={10000}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div>
                                <label className="label">Content Tone</label>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                    {contentTones.map((tone) => (
                                        <button
                                            key={tone.value}
                                            onClick={() => setData({
                                                ...data,
                                                aiSettings: { ...data.aiSettings, contentTone: tone.value }
                                            })}
                                            className={`p-3 rounded-lg border text-sm transition-all ${data.aiSettings.contentTone === tone.value
                                                ? 'border-primary-500 bg-primary-500/10 text-primary-400'
                                                : 'border-dark-700 hover:border-dark-600'
                                                }`}
                                        >
                                            {tone.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <label className="label">Target Audience Description</label>
                                <textarea
                                    value={data.aiSettings.targetAudience}
                                    onChange={(e) => setData({
                                        ...data,
                                        aiSettings: { ...data.aiSettings, targetAudience: e.target.value }
                                    })}
                                    className="input min-h-[80px]"
                                    placeholder="e.g., Small business owners aged 25-45 looking to grow their online presence..."
                                />
                                <p className="text-xs text-dark-500 mt-1">
                                    This helps AI tailor content for your audience
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Budget Settings */}
                    {activeTab === 'budget' && (
                        <div className="card space-y-6">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl flex items-center justify-center">
                                    <DollarSign className="w-5 h-5 text-white" />
                                </div>
                                <div>
                                    <h2 className="text-xl font-semibold">Budget Settings</h2>
                                    <p className="text-sm text-dark-400">Manage your project budget and spending</p>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="label">Monthly Budget</label>
                                    <div className="relative">
                                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-dark-400">
                                            {data.currency === 'USD' ? '$' : data.currency === 'EUR' ? '€' : data.currency === 'GBP' ? '£' : '$'}
                                        </span>
                                        <input
                                            type="number"
                                            value={data.monthlyBudget || ''}
                                            onChange={(e) => setData({ ...data, monthlyBudget: parseFloat(e.target.value) || null })}
                                            className="input pl-8"
                                            placeholder="1000"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="label">Currency</label>
                                    <select
                                        value={data.currency}
                                        onChange={(e) => setData({ ...data, currency: e.target.value })}
                                        className="input"
                                    >
                                        {currencies.map((cur) => (
                                            <option key={cur} value={cur}>{cur}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* Budget Overview */}
                            <div className="p-6 bg-dark-800/50 rounded-xl">
                                <h3 className="font-medium mb-4">Budget Overview</h3>
                                <div className="grid grid-cols-3 gap-6">
                                    <div>
                                        <p className="text-dark-400 text-sm">Monthly Budget</p>
                                        <p className="text-2xl font-bold">
                                            ${data.monthlyBudget?.toLocaleString() || '0'}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-dark-400 text-sm">Spent This Month</p>
                                        <p className="text-2xl font-bold text-orange-400">
                                            ${project.budgetSpent?.toLocaleString() || '0'}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-dark-400 text-sm">Remaining</p>
                                        <p className="text-2xl font-bold text-green-400">
                                            ${((data.monthlyBudget || 0) - (project.budgetSpent || 0)).toLocaleString()}
                                        </p>
                                    </div>
                                </div>

                                {data.monthlyBudget && (
                                    <div className="mt-4">
                                        <div className="flex justify-between text-sm mb-1">
                                            <span>Budget Used</span>
                                            <span>{Math.round(((project.budgetSpent || 0) / data.monthlyBudget) * 100)}%</span>
                                        </div>
                                        <div className="h-2 bg-dark-700 rounded-full overflow-hidden">
                                            <div
                                                className="h-full bg-gradient-to-r from-green-500 to-emerald-500 transition-all"
                                                style={{ width: `${Math.min(((project.budgetSpent || 0) / data.monthlyBudget) * 100, 100)}%` }}
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                                <p className="text-sm text-blue-400">
                                    💡 Set a budget to receive alerts when spending approaches limits
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Integrations */}
                    {activeTab === 'integrations' && (
                        <div className="card space-y-6">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center">
                                    <Webhook className="w-5 h-5 text-white" />
                                </div>
                                <div>
                                    <h2 className="text-xl font-semibold">Integrations</h2>
                                    <p className="text-sm text-dark-400">Connect external services to this project</p>
                                </div>
                            </div>

                            {/* Project API Key */}
                            <div className="p-4 bg-dark-800/50 rounded-lg">
                                <div className="flex items-center justify-between mb-2">
                                    <h3 className="font-medium">Project API Key</h3>
                                    <button className="btn btn-secondary text-sm">Generate New Key</button>
                                </div>
                                <div className="flex items-center gap-2">
                                    <code className="flex-1 p-3 bg-dark-900 rounded text-sm text-dark-300 font-mono">
                                        proj_{projectId?.slice(0, 8)}...****
                                    </code>
                                    <button className="p-2 hover:bg-dark-700 rounded" title="Copy">
                                        <Copy className="w-4 h-4" />
                                    </button>
                                </div>
                                <p className="text-xs text-dark-500 mt-2">
                                    Use this key to access project-specific API endpoints
                                </p>
                            </div>

                            {/* Available Integrations */}
                            <div>
                                <h3 className="font-medium mb-4">Available Integrations</h3>
                                <div className="space-y-3">
                                    {[
                                        { name: 'Google Analytics', icon: '📊', connected: false, description: 'Import traffic data' },
                                        { name: 'Google Search Console', icon: '🔍', connected: false, description: 'SEO performance data' },
                                        { name: 'Facebook Ads', icon: '📘', connected: false, description: 'Ad campaign management' },
                                        { name: 'Google Ads', icon: '📢', connected: false, description: 'Ad campaign management' },
                                        { name: 'Mailchimp', icon: '📧', connected: false, description: 'Email marketing sync' },
                                        { name: 'Slack', icon: '💬', connected: false, description: 'Notifications & alerts' },
                                    ].map((integration) => (
                                        <div
                                            key={integration.name}
                                            className="flex items-center justify-between p-4 bg-dark-800/50 rounded-lg"
                                        >
                                            <div className="flex items-center gap-3">
                                                <span className="text-2xl">{integration.icon}</span>
                                                <div>
                                                    <p className="font-medium">{integration.name}</p>
                                                    <p className="text-sm text-dark-400">{integration.description}</p>
                                                </div>
                                            </div>
                                            {integration.connected ? (
                                                <span className="flex items-center gap-1 text-green-400 text-sm">
                                                    <CheckCircle className="w-4 h-4" /> Connected
                                                </span>
                                            ) : (
                                                <button className="btn btn-secondary text-sm">Connect</button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Webhooks */}
                            <div>
                                <h3 className="font-medium mb-4">Webhooks</h3>
                                <div className="p-4 bg-dark-800/50 rounded-lg">
                                    <p className="text-dark-400 text-sm mb-4">
                                        Receive real-time notifications when events occur in this project.
                                    </p>
                                    <button className="btn btn-secondary">
                                        <Webhook className="w-4 h-4 mr-2" />
                                        Add Webhook Endpoint
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Danger Zone */}
                    {activeTab === 'danger' && (
                        <div className="card border-red-500/30 space-y-6">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-red-500/20 rounded-xl flex items-center justify-center">
                                    <AlertCircle className="w-5 h-5 text-red-400" />
                                </div>
                                <div>
                                    <h2 className="text-xl font-semibold text-red-400">Danger Zone</h2>
                                    <p className="text-sm text-dark-400">Irreversible actions</p>
                                </div>
                            </div>

                            <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                                <h3 className="font-medium text-red-400 mb-2">Delete Project</h3>
                                <p className="text-sm text-dark-300 mb-4">
                                    Permanently delete this project and all its data. This action cannot be undone.
                                </p>

                                <div className="space-y-3">
                                    <div>
                                        <label className="text-sm text-dark-400 mb-1 block">
                                            Type <strong className="text-white">{data.name}</strong> to confirm:
                                        </label>
                                        <input
                                            type="text"
                                            value={deleteConfirm}
                                            onChange={(e) => setDeleteConfirm(e.target.value)}
                                            className="input bg-dark-900 border-red-500/30"
                                            placeholder="Project name"
                                        />
                                    </div>
                                    <button
                                        onClick={handleDelete}
                                        disabled={deleteConfirm !== data.name || deleteMutation.isPending}
                                        className="btn bg-red-600 hover:bg-red-700 text-white disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                    >
                                        {deleteMutation.isPending ? (
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                        ) : (
                                            <Trash2 className="w-4 h-4" />
                                        )}
                                        Delete Project Permanently
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
